import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { Heart, Users, BookOpen, Shield, FileText, ClipboardList, ArrowRight, Quote } from "lucide-react";

const HERO_IMG = "/hero.png";

const whoWeServe = [
  {
    icon: Users,
    title: "Homeschool Families",
    desc: "Families looking for a professionally built church school system for their educational program.",
  },
  {
    icon: Shield,
    title: "Churches Seeking Structure",
    desc: "Congregations looking to launch their own organized educational programs.",
  },
  {
    icon: BookOpen,
    title: "Adults Continuing Education",
    desc: "Adult learners pursuing growth within a faith-based educational framework.",
  },
];

const whatWeDo = [
  {
    icon: Shield,
    title: "Church School Covering",
    desc: "We build complete church school systems — websites, enrollment portals, and admin dashboards for your school.",
  },
  {
    icon: FileText,
    title: "Records & Enrollment",
    desc: "We maintain thorough student records and manage enrollment processes with care.",
  },
  {
    icon: ClipboardList,
    title: "Structured Education Systems",
    desc: "We support the development and operation of well-organized educational programs.",
  },
];

const dailyVerses = [
  { ref: "Psalm 23:1-3", text: "The LORD is my shepherd; I shall not want. He maketh me to lie down in green pastures: he leadeth me beside the still waters. He restoreth my soul: he leadeth me in the paths of righteousness for his name's sake." },
  { ref: "Proverbs 3:5-6", text: "Trust in the LORD with all thine heart; and lean not unto thine own understanding. In all thy ways acknowledge him, and he shall direct thy paths." },
  { ref: "Jeremiah 29:11", text: "For I know the thoughts that I think toward you, saith the LORD, thoughts of peace, and not of evil, to give you an expected end." },
  { ref: "Philippians 4:13", text: "I can do all things through Christ which strengtheneth me." },
  { ref: "Isaiah 40:31", text: "But they that wait upon the LORD shall renew their strength; they shall mount up with wings as eagles; they shall run, and not be weary; and they shall walk, and not faint." },
  { ref: "Romans 8:28", text: "And we know that all things work together for good to them that love God, to them who are the called according to his purpose." },
  { ref: "Joshua 1:9", text: "Have not I commanded thee? Be strong and of a good courage; be not afraid, neither be thou dismayed: for the LORD thy God is with thee whithersoever thou goest." },
  { ref: "John 3:16", text: "For God so loved the world, that he gave his only begotten Son, that whosoever believeth in him should not perish, but have everlasting life." },
  { ref: "Psalm 46:1", text: "God is our refuge and strength, a very present help in trouble." },
  { ref: "Matthew 11:28", text: "Come unto me, all ye that labour and are heavy laden, and I will give you rest." },
  { ref: "2 Timothy 1:7", text: "For God hath not given us the spirit of fear; but of power, and of love, and of a sound mind." },
  { ref: "Isaiah 41:10", text: "Fear thou not; for I am with thee: be not dismayed; for I am thy God: I will strengthen thee; yea, I will help thee; yea, I will uphold thee with the right hand of my righteousness." },
  { ref: "Psalm 119:105", text: "Thy word is a lamp unto my feet, and a light unto my path." },
  { ref: "Romans 12:2", text: "And be not conformed to this world: but be ye transformed by the renewing of your mind, that ye may prove what is that good, and acceptable, and perfect, will of God." },
  { ref: "Hebrews 11:1", text: "Now faith is the substance of things hoped for, the evidence of things not seen." },
  { ref: "Ephesians 2:8-9", text: "For by grace are ye saved through faith; and that not of yourselves: it is the gift of God: Not of works, lest any man should boast." },
  { ref: "Psalm 37:4", text: "Delight thyself also in the LORD; and he shall give thee the desires of thine heart." },
  { ref: "Galatians 5:22-23", text: "But the fruit of the Spirit is love, joy, peace, longsuffering, gentleness, goodness, faith, meekness, temperance: against such there is no law." },
  { ref: "1 Peter 5:7", text: "Casting all your care upon him; for he careth for you." },
  { ref: "Psalm 91:1-2", text: "He that dwelleth in the secret place of the most High shall abide under the shadow of the Almighty. I will say of the LORD, He is my refuge and my fortress: my God; in him will I trust." },
  { ref: "Colossians 3:23", text: "And whatsoever ye do, do it heartily, as to the Lord, and not unto men." },
  { ref: "Proverbs 16:3", text: "Commit thy works unto the LORD, and thy thoughts shall be established." },
  { ref: "Psalm 139:14", text: "I will praise thee; for I am fearfully and wonderfully made: marvellous are thy works; and that my soul knoweth right well." },
  { ref: "Romans 5:8", text: "But God commendeth his love toward us, in that, while we were yet sinners, Christ died for us." },
  { ref: "Isaiah 26:3", text: "Thou wilt keep him in perfect peace, whose mind is stayed on thee: because he trusteth in thee." },
  { ref: "Matthew 6:33", text: "But seek ye first the kingdom of God, and his righteousness; and all these things shall be added unto you." },
  { ref: "Psalm 121:1-2", text: "I will lift up mine eyes unto the hills, from whence cometh my help. My help cometh from the LORD, which made heaven and earth." },
  { ref: "John 14:6", text: "Jesus saith unto him, I am the way, the truth, and the life: no man cometh unto the Father, but by me." },
  { ref: "1 Corinthians 13:4-7", text: "Charity suffereth long, and is kind; charity envieth not; charity vaunteth not itself, is not puffed up, doth not behave itself unseemly, seeketh not her own, is not easily provoked, thinketh no evil; rejoiceth not in iniquity, but rejoiceth in the truth; beareth all things, believeth all things, hopeth all things, endureth all things." },
  { ref: "Psalm 34:8", text: "O taste and see that the LORD is good: blessed is the man that trusteth in him." },
  { ref: "Psalm 27:1", text: "The LORD is my light and my salvation; whom shall I fear? the LORD is the strength of my life; of whom shall I be afraid?" },
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    axios.get(`${API}/testimonials`).then(res => setTestimonials(res.data)).catch(() => {});
  }, []);

  if (testimonials.length === 0) return null;

  return (
    <section data-testid="testimonials-section" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-14">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">
            Testimonials
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#1C2E4A]">
            What Families Are Saying
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.slice(0, 6).map((t, i) => (
            <div key={t.id || i} data-testid={`testimonial-${i}`} className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] p-6 flex flex-col">
              <Quote className="w-6 h-6 text-[#C8A97E]/40 mb-3" />
              <p className="text-sm text-[#595959] leading-relaxed flex-1 italic">
                &ldquo;{t.quote}&rdquo;
              </p>
              <div className="mt-4 pt-4 border-t border-[#E5E2DC]">
                <p className="text-sm font-medium text-[#1C2E4A]">{t.name}</p>
                <p className="text-xs text-[#595959]">{t.role}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function VerseOfTheDay() {
  const dayIndex = new Date().getDate() % dailyVerses.length;
  const verse = dailyVerses[dayIndex];

  return (
    <section data-testid="verse-of-day-section" className="py-16 md:py-20 bg-[#FAF9F6]">
      <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
        <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">
          Verse of the Day
        </p>
        <blockquote className="font-serif text-xl sm:text-2xl text-[#1C2E4A] leading-relaxed italic mb-4">
          &ldquo;{verse.text}&rdquo;
        </blockquote>
        <p className="text-sm font-medium text-[#C8A97E]">&mdash; {verse.ref} (KJV)</p>
        <Link
          to="/bible"
          data-testid="verse-read-more"
          className="inline-flex items-center gap-2 mt-6 text-sm text-[#1C2E4A] hover:text-[#C8A97E] font-medium transition-colors"
        >
          Read the Bible <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section data-testid="hero-section" className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="ACS Virtual Church Interior"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#1C2E4A]/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-28 md:py-40 lg:py-48">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-4 animate-fade-in-up opacity-0 stagger-1">
              ACS Virtual Church
            </p>
            <h1
              data-testid="hero-headline"
              className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 animate-fade-in-up opacity-0 stagger-2"
            >
              A Church Supporting Families Through Faith and Education
            </h1>
            <p className="text-base md:text-lg text-white/80 leading-relaxed mb-8 max-w-lg animate-fade-in-up opacity-0 stagger-3">
              Rooted in faith, dedicated to family, and committed to providing
              structured education through professionally built school systems.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up opacity-0 stagger-4">
              <Link
                to="/about"
                data-testid="hero-cta-learn"
                className="bg-[#C8A97E] text-white hover:bg-[#B3956A] px-8 py-3 rounded-md transition-colors font-medium inline-flex items-center gap-2"
              >
                Learn More <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-contact"
                className="border-2 border-white/60 text-white hover:bg-white/10 px-8 py-3 rounded-md transition-all font-medium"
              >
                Get in Touch
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section data-testid="mission-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">
            Our Mission
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#1C2E4A] mb-6 max-w-3xl mx-auto">
            Building a Foundation of Faith, Family, and Education
          </h2>
          <p className="text-base text-[#595959] leading-relaxed max-w-2xl mx-auto">
            ACS Virtual Church exists to serve families and churches by providing
            a faith-based structure for education. We believe every child deserves
            to learn in an environment that nurtures their spirit, strengthens
            their family bonds, and equips them for the future.
          </p>

          <div className="mt-14 flex flex-wrap justify-center gap-10 md:gap-16">
            {[
              { icon: Heart, label: "Faith" },
              { icon: Users, label: "Family" },
              { icon: BookOpen, label: "Education" },
            ].map((item) => (
              <div key={item.label} className="flex flex-col items-center gap-3">
                <div className="w-14 h-14 rounded-full bg-[#1C2E4A]/5 flex items-center justify-center">
                  <item.icon className="w-6 h-6 text-[#1C2E4A]" />
                </div>
                <span className="font-serif text-lg font-medium text-[#1C2E4A]">
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section data-testid="who-we-serve-section" className="py-20 md:py-28 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">
            Who We Serve
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#1C2E4A] mb-12">
            Our Community
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {whoWeServe.map((item, i) => (
              <div
                key={item.title}
                data-testid={`who-we-serve-card-${i}`}
                className="bg-white rounded-xl border border-[#E5E2DC] shadow-sm hover:shadow-md hover:-translate-y-1 transition-all p-8"
              >
                <div className="w-12 h-12 rounded-lg bg-[#1C2E4A]/5 flex items-center justify-center mb-5">
                  <item.icon className="w-5 h-5 text-[#1C2E4A]" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-[#1C2E4A] mb-3">
                  {item.title}
                </h3>
                <p className="text-sm text-[#595959] leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section data-testid="what-we-do-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">
            What We Do
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#1C2E4A] mb-12">
            Our Services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {whatWeDo.map((item, i) => (
              <div
                key={item.title}
                data-testid={`what-we-do-card-${i}`}
                className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] p-8 hover:shadow-md hover:-translate-y-1 transition-all"
              >
                <div className="w-12 h-12 rounded-lg bg-[#C8A97E]/10 flex items-center justify-center mb-5">
                  <item.icon className="w-5 h-5 text-[#C8A97E]" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-[#1C2E4A] mb-3">
                  {item.title}
                </h3>
                <p className="text-sm text-[#595959] leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* Verse of the Day */}
      <VerseOfTheDay />

      {/* CTA */}
      <section data-testid="cta-section" className="py-20 md:py-28 bg-[#1C2E4A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mb-5">
            Ready to Learn More?
          </h2>
          <p className="text-base text-white/70 max-w-xl mx-auto mb-8">
            Whether you are a family looking for a church school or a church
            wanting to launch your own school, we are here to help.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/church-school-program"
              data-testid="cta-program"
              className="bg-[#C8A97E] text-white hover:bg-[#B3956A] px-8 py-3 rounded-md transition-colors font-medium"
            >
              Explore Our Program
            </Link>
            <Link
              to="/contact"
              data-testid="cta-contact"
              className="border-2 border-white/40 text-white hover:bg-white/10 px-8 py-3 rounded-md transition-all font-medium"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
